/**
 * Storage utilities for troubleshooting system
 *
 * Uses localStorage to persist the troubleshooting enabled state.
 * Key: 'ffTroubleshootEnabled'
 * Default: disabled (must be explicitly enabled via console commands)
 */

const STORAGE_KEY = 'ffTroubleshootEnabled';

/**
 * Check if we are running in a test environment (Jest).
 * Troubleshooting stays off in tests to avoid noise and overhead.
 */
function isTestEnvironment(): boolean {
  return (
    typeof process !== 'undefined' &&
    (process.env?.NODE_ENV === 'test' ||
      typeof process.env?.JEST_WORKER_ID !== 'undefined')
  );
}

/**
 * Whether FormForge troubleshooting capture is active.
 * Always off under Jest. In the browser, reads from localStorage
 * (defaults to disabled if not explicitly enabled).
 */
export function isTroubleshootingEnabled(): boolean {
  if (isTestEnvironment()) {
    return false;
  }

  try {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    }
  } catch {
    // localStorage may not be available (SSR, security restrictions)
  }

  return false;
}

/**
 * Enable troubleshooting and persist the setting.
 * Also enables the tracker singleton if it exists.
 */
export function enableTroubleshooting(): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, 'true');
    }
  } catch {
    // localStorage may not be available
  }

  // Sync the in-memory tracker state immediately
  syncTrackerEnabled(true);
}

/**
 * Disable troubleshooting and persist the setting.
 * Also disables the tracker singleton if it exists.
 */
export function disableTroubleshooting(): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, 'false');
    }
  } catch {
    // localStorage may not be available
  }

  // Sync the in-memory tracker state immediately
  syncTrackerEnabled(false);
}

/**
 * Toggle troubleshooting state. Returns the new enabled state.
 */
export function toggleTroubleshooting(): boolean {
  const newState = !isTroubleshootingEnabled();

  if (newState) {
    enableTroubleshooting();
  } else {
    disableTroubleshooting();
  }

  return newState;
}

/**
 * Sync the tracker singleton's enabled state without creating a
 * circular import. We lazily import the tracker only when needed.
 */
function syncTrackerEnabled(enabled: boolean): void {
  try {
    // Dynamic require to avoid circular dependency with TroubleshootingTracker
    // which imports isTroubleshootingEnabled from this file.
    // eslint-disable-next-line @typescript-eslint/no-require-imports, global-require, @typescript-eslint/no-var-requires, @typescript-eslint/no-unsafe-assignment
    const { getTroubleshootingTracker } = require('./TroubleshootingTracker');
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const tracker = getTroubleshootingTracker();

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    tracker.setEnabled(enabled);
  } catch {
    // Tracker may not be initialized yet — that's fine,
    // it will read from isTroubleshootingEnabled() on construction.
  }
}
